// The in-game pause overlay: Resume / Settings / Quit. DOM-only — it owns no
// game state and decides nothing; `scenes/PauseHost.ts` supplies the callbacks
// and is what actually freezes the table.
//
// Two views swap inside the one card: main (Resume + Quit) and settings (the
// same SettingsPanel the menu's Settings screen builds, plus Back). The table
// stays paused across the swap, so a language or coach change applies to the
// hand the player is standing in rather than the next one.
//
// Modelled on sudoku's `ui/PauseMenu.ts`, minus what mahjong has no equivalent
// of (Share, Restart, Solve). If a third game grows one of these, THAT is the
// signal to lift the shared shell into `@bn-games/ui-shell` — two is a pattern,
// not yet a package.

import {
  createCrestButton,
  createSettingsPanel,
  el,
  installFocusTrap,
  type FocusTrapHandle,
  type SettingsRow,
} from '@bn-games/ui-shell';
import { t } from '../i18n/I18n.js';

export interface PauseMenuCallbacks {
  readonly onResume: () => void;
  /** Leave the table for the menu — the old bare-Escape behaviour, now a
   *  deliberate second click. */
  readonly onQuit: () => void;
  /** Re-read on every render so the rows always show live values. */
  readonly getSettingsRows: () => readonly SettingsRow[];
}

type View = 'main' | 'settings';

let pauseTitleIdSeq = 0;

export class PauseMenu {
  private overlayNode: HTMLElement | null = null;
  private titleNode: HTMLElement | null = null;
  private bodyNode: HTMLElement | null = null;
  private actionsNode: HTMLElement | null = null;
  private trap: FocusTrapHandle | null = null;
  private view: View = 'main';

  constructor(private readonly callbacks: PauseMenuCallbacks) {}

  isOpen(): boolean {
    return this.overlayNode !== null;
  }

  open(): void {
    if (this.isOpen()) return;
    const titleId = `mahjong-pause-title-${++pauseTitleIdSeq}`;

    const overlay = el('div', { class: 'modal-overlay' });
    const card = el('div', {
      class: 'modal-card',
      attrs: {
        role: 'dialog',
        'aria-modal': 'true',
        'aria-labelledby': titleId,
        tabindex: '-1',
      },
    });

    this.titleNode = el('h2', { class: 'modal-title', text: '', attrs: { id: titleId } });
    this.bodyNode = el('div', { class: 'modal-body' });
    this.actionsNode = el('div', { class: 'modal-actions' });
    card.append(this.titleNode, this.bodyNode, this.actionsNode);

    overlay.appendChild(card);
    // Clicking the dimmed felt around the card resumes — the same escape hatch
    // Escape itself offers, and never the destructive one.
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) this.handleResume();
    });

    document.body.appendChild(overlay);
    this.overlayNode = overlay;
    this.view = 'main';
    this.renderView();
    this.trap = installFocusTrap(card);
  }

  /** Tear the overlay down without resuming — the caller owns what happens
   *  next (see `close` vs `handleResume`). Idempotent. */
  close(): void {
    if (this.trap !== null) {
      this.trap.release();
      this.trap = null;
    }
    if (this.overlayNode !== null) {
      this.overlayNode.remove();
      this.overlayNode = null;
      this.titleNode = null;
      this.bodyNode = null;
      this.actionsNode = null;
    }
    this.view = 'main';
  }

  toggle(): void {
    if (this.isOpen()) this.handleResume();
    else this.open();
  }

  /**
   * Repaint the open card. The settings panel re-reads its own rows after
   * every click, so this is only for changes that land from *outside* it —
   * chiefly a language switch, whose new locale arrives one microtask later
   * and has to reach the card's own titles and buttons too, not just the rows.
   */
  refresh(): void {
    this.renderView();
  }

  private renderView(): void {
    const { titleNode, bodyNode, actionsNode } = this;
    if (titleNode === null || bodyNode === null || actionsNode === null) return;
    bodyNode.replaceChildren();
    actionsNode.replaceChildren();
    if (this.view === 'main') this.renderMain(titleNode, bodyNode, actionsNode);
    else this.renderSettings(titleNode, bodyNode, actionsNode);
  }

  private renderMain(title: HTMLElement, body: HTMLElement, actions: HTMLElement): void {
    title.textContent = t('pause.title');
    // Settings stays inside the modal — swap views, don't close, so the table
    // is still paused underneath.
    body.appendChild(
      this.row(
        createCrestButton({
          label: t('pause.settings'),
          small: true,
          onClick: () => this.setView('settings'),
        }),
      ),
    );
    body.appendChild(
      this.row(
        createCrestButton({
          label: t('pause.quit'),
          small: true,
          onClick: () => {
            this.close();
            this.callbacks.onQuit();
          },
        }),
      ),
    );
    actions.appendChild(
      createCrestButton({
        label: t('pause.resume'),
        primary: true,
        small: true,
        onClick: () => this.handleResume(),
      }),
    );
  }

  private renderSettings(title: HTMLElement, body: HTMLElement, actions: HTMLElement): void {
    title.textContent = t('settings.title');
    body.appendChild(
      createSettingsPanel({ getRows: () => this.callbacks.getSettingsRows() }).element,
    );
    actions.appendChild(
      createCrestButton({
        label: t('common.back'),
        small: true,
        onClick: () => this.setView('main'),
      }),
    );
    actions.appendChild(
      createCrestButton({
        label: t('pause.resume'),
        primary: true,
        small: true,
        onClick: () => this.handleResume(),
      }),
    );
  }

  private setView(view: View): void {
    this.view = view;
    this.renderView();
  }

  private handleResume(): void {
    this.close();
    this.callbacks.onResume();
  }

  private row(control: HTMLElement): HTMLElement {
    return el('div', { class: 'game-overlay-section game-overlay-section--action' }, [control]);
  }
}
